import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  type Timestamp,
} from 'firebase/firestore'
import { firestoreDb, firebaseAuth } from './firebase'
import { callable } from './callable'
import { isEduDemoWorkspace } from './eduDemo/eduDemoWorkspaces'
import {
  demoListLearners,
  demoListLearnersForMember,
  demoGetLearnerContact,
  demoListActiveEnrolmentsForNodes,
  demoListActiveEnrolmentsForLearners,
} from './eduDemo/eduDemoDirectReads'
import type { EduEnrollment } from '../types/schema'

export type CreateLearnerRow = {
  displayName: string
  isMinor?: boolean
  dob?: string
  admissionNo?: string
  nodeId?: string
  rollNo?: string
  guardianEmail?: string
  guardianPhone?: string
}

export type CreateLearnersInput = {
  workspaceId: string
  rows: CreateLearnerRow[]
  institutionAttests?: boolean
}

export type CreatedLearnerResult = {
  learnerId: string
  displayName: string
  joinCodeId: string
  code: string
  pin: string
  expiresAtMs: number
}

export type CreateLearnersResponse = {
  batchId: string
  learners: CreatedLearnerResult[]
}

export type EduLearnerStatus = 'ACTIVE' | 'LEFT' | 'ALUMNI'

export type EduLearnerDoc = {
  id: string
  workspaceId: string
  displayName: string
  isMinor: boolean
  dob?: string
  admissionNo?: string
  status: EduLearnerStatus
  guardianUids: string[]
  selfUid: string | null
  currentNodeIds: string[]
  readAccess: string[]
  consent?: {
    institutionAttestedAt?: Timestamp | { seconds: number; nanoseconds: number }
    institutionAttestedBy?: string
  }
  joinCodeId: string
  frozen?: boolean
  frozenAt?: Timestamp | { seconds: number; nanoseconds: number } | null
  consentWithdrawnAt?: Timestamp | { seconds: number; nanoseconds: number } | null
  leftAt?: Timestamp | { seconds: number; nanoseconds: number } | null
  erased?: boolean
  erasedAt?: Timestamp | { seconds: number; nanoseconds: number } | null
  pseudonym?: string
  createdAt: Timestamp | { seconds: number; nanoseconds: number } | null
  createdBy: string
}

export const createLearners = async (
  input: CreateLearnersInput,
): Promise<CreateLearnersResponse> =>
  callable<CreateLearnersInput, CreateLearnersResponse>('createLearners', input)

export const listLearners = async (workspaceId: string): Promise<EduLearnerDoc[]> => {
  if (isEduDemoWorkspace(workspaceId)) {
    return demoListLearners(workspaceId)
  }

  const q = query(
    collection(firestoreDb, 'edu_learners'),
    where('workspaceId', '==', workspaceId),
    orderBy('createdAt', 'desc'),
    limit(500),
  )
  const snapshot = await getDocs(q)
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...(docSnap.data() as Omit<EduLearnerDoc, 'id'>),
  }))
}

export const listLearnersForMember = async (
  workspaceId: string,
  uid: string,
): Promise<EduLearnerDoc[]> => {
  if (isEduDemoWorkspace(workspaceId)) {
    return demoListLearnersForMember(workspaceId, uid)
  }

  const q = query(
    collection(firestoreDb, 'edu_learners'),
    where('workspaceId', '==', workspaceId),
    where('readAccess', 'array-contains', uid),
    orderBy('createdAt', 'desc'),
    limit(500),
  )
  const snapshot = await getDocs(q)
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...(docSnap.data() as Omit<EduLearnerDoc, 'id'>),
  }))
}

export type CreateGuardianCodeInput = {
  learnerId: string
}

export type CreateGuardianCodeResponse = {
  joinCodeId: string
  code: string
  pin: string
  expiresAtMs: number
}

export const createGuardianCode = async (
  learnerId: string,
): Promise<CreateGuardianCodeResponse> =>
  callable<CreateGuardianCodeInput, CreateGuardianCodeResponse>('createGuardianCode', { learnerId })

export type WithdrawConsentInput = {
  learnerId: string
  guardianUid?: string
}

export type WithdrawConsentResponse = {
  success: boolean
}

export const withdrawConsent = async (
  learnerId: string,
  guardianUid?: string,
): Promise<WithdrawConsentResponse> =>
  callable<WithdrawConsentInput, WithdrawConsentResponse>('withdrawConsent', { learnerId, guardianUid })

export type SetEduLearnerStatusInput = {
  workspaceId: string
  learnerId: string
  status: EduLearnerStatus
  opId?: string
}

export type SetEduLearnerStatusResult = {
  success: boolean
  learnerId: string
  status: EduLearnerStatus
  leftAt?: Timestamp | { seconds: number; nanoseconds: number } | null
  replayed?: boolean
}

export const setEduLearnerStatus = async (
  input: SetEduLearnerStatusInput,
): Promise<SetEduLearnerStatusResult> =>
  callable<SetEduLearnerStatusInput, SetEduLearnerStatusResult>('setEduLearnerStatus', input)

export type EduConsentDoc = {
  learnerId: string
  workspaceId: string
  guardianUid: string
  basis?: 'CONSENT' | 'PUBLIC_TASK_OR_CONTRACT'
  parentScreenMode?: 'CONSENT_CHECKBOX' | 'INFO_NOTICE'
  retentionDays?: number
  noticeVersion?: string
  withdrawnAt?: Timestamp | { seconds: number; nanoseconds: number } | null
  objectedAt?: Timestamp | { seconds: number; nanoseconds: number } | null
  acceptedAt?: Timestamp | { seconds: number; nanoseconds: number } | null
}

export const getEduConsent = async (
  workspaceId: string,
  learnerId: string,
  guardianUid?: string,
): Promise<EduConsentDoc | null> => {
  if (!workspaceId || !learnerId || isEduDemoWorkspace(workspaceId)) return null
  const uid = guardianUid || firebaseAuth.currentUser?.uid
  if (!uid) return null
  const consentRef = doc(firestoreDb, 'edu_consents', `${learnerId}_${uid}`)
  const snap = await getDoc(consentRef)
  if (!snap.exists()) return null
  return snap.data() as EduConsentDoc
}

export type EduLearnerContactDoc = {
  id: string
  workspaceId: string
  learnerId: string
  guardianEmail?: string
  guardianPhone?: string
  readAccess: string[]
  createdAt: Timestamp | { seconds: number; nanoseconds: number } | null
  createdBy: string
}

export const getLearnerContact = async (
  learnerId: string,
): Promise<EduLearnerContactDoc | null> => {
  const demoResult = await demoGetLearnerContact(learnerId)
  if (demoResult) return demoResult

  const docRef = doc(firestoreDb, 'edu_learner_contacts', learnerId)
  const snap = await getDoc(docRef)
  if (!snap.exists()) {
    return null
  }
  return {
    id: snap.id,
    ...(snap.data() as Omit<EduLearnerContactDoc, 'id'>),
  }
}

const chunkArray = <T>(arr: T[], size: number): T[][] => {
  const chunks: T[][] = []
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size))
  }
  return chunks
}

export const listActiveEnrolmentsForNodes = async (
  workspaceId: string,
  nodeIds: string[],
): Promise<EduEnrollment[]> => {
  if (!workspaceId || nodeIds.length === 0) return []
  if (isEduDemoWorkspace(workspaceId)) {
    return demoListActiveEnrolmentsForNodes(workspaceId, nodeIds)
  }
  const chunks = chunkArray(nodeIds, 30)
  const results = await Promise.all(
    chunks.map(async (chunk) => {
      const q = query(
        collection(firestoreDb, 'edu_enrollments'),
        where('workspaceId', '==', workspaceId),
        where('nodeId', 'in', chunk),
        where('to', '==', null),
      )
      const snap = await getDocs(q)
      return snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() as Omit<EduEnrollment, 'id'>),
      }))
    }),
  )
  return results.flat()
}

export const listActiveEnrolmentsForLearners = async (
  workspaceId: string,
  learnerIds: string[],
): Promise<EduEnrollment[]> => {
  if (!workspaceId || learnerIds.length === 0) return []
  if (isEduDemoWorkspace(workspaceId)) {
    return demoListActiveEnrolmentsForLearners(workspaceId, learnerIds)
  }
  const chunks = chunkArray(learnerIds, 30)
  const results = await Promise.all(
    chunks.map(async (chunk) => {
      const q = query(
        collection(firestoreDb, 'edu_enrollments'),
        where('workspaceId', '==', workspaceId),
        where('learnerId', 'in', chunk),
        where('to', '==', null),
      )
      const snap = await getDocs(q)
      return snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() as Omit<EduEnrollment, 'id'>),
      }))
    }),
  )
  return results.flat()
}


